import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { toast } from "sonner";

const BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ── soft bell chime (Web Audio, no asset needed) ───────────────────────────
let _ctx = null;
export function playChime() {
  try {
    _ctx = _ctx || new (window.AudioContext || window.webkitAudioContext)();
    if (_ctx.state === "suspended") _ctx.resume();
    const t = _ctx.currentTime;
    [[830, 0], [1108, 0.14]].forEach(([freq, delay]) => {
      const o = _ctx.createOscillator();
      const g = _ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t + delay);
      g.gain.linearRampToValueAtTime(0.16, t + delay + 0.025);
      g.gain.exponentialRampToValueAtTime(0.0001, t + delay + 1.15);
      o.connect(g);
      g.connect(_ctx.destination);
      o.start(t + delay);
      o.stop(t + delay + 1.2);
    });
  } catch { /* audio unavailable */ }
}

export function showDesktopToast(n) {
  try {
    const note = new Notification(`Kairos — ${n.title || "Notification"}`, {
      body: n.body || "",
      tag: n.id,
      silent: true,
    });
    note.onclick = () => { window.focus(); note.close(); };
    setTimeout(() => note.close(), 8000);
  } catch { /* Notification constructor unavailable */ }
}

const SEV_TOAST = { success: "success", warning: "warning", error: "error", info: "info" };

// ── title-bar blink while the tab is in the background ─────────────────────
let _blinkTimer = null, _baseTitle = null, _unseen = 0;
function startTitleBlink() {
  _unseen += 1;
  if (_blinkTimer) return;
  _baseTitle = document.title;
  let flip = false;
  _blinkTimer = setInterval(() => {
    flip = !flip;
    document.title = flip ? `🔔 (${_unseen}) New alert — Kairos` : _baseTitle;
  }, 1100);
}
function stopTitleBlink() {
  if (!_blinkTimer) return;
  clearInterval(_blinkTimer);
  _blinkTimer = null;
  _unseen = 0;
  if (_baseTitle) document.title = _baseTitle;
}
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => { if (!document.hidden) stopTitleBlink(); });
  window.addEventListener("focus", stopTitleBlink);
}

// ── main hook: SSE stream → in-app toast + OS toast + chime ────────────────
export function useNotificationStream(onNotification) {
  const prefsRef = useRef({
    desktop_enabled: true, sound_enabled: true, desktop_categories: [],
  });
  const cbRef = useRef(onNotification);
  cbRef.current = onNotification;
  const qcRef = useRef(null);
  qcRef.current = useQueryClient();

  const loadPrefs = useCallback(async () => {
    try {
      const r = await api.get("/notifications/prefs");
      prefsRef.current = r.data;
    } catch { /* ignore */ }
  }, []);

  // load prefs on mount + whenever the prefs panel saves
  useEffect(() => {
    loadPrefs();
    const h = () => loadPrefs();
    window.addEventListener("kairos:prefs-updated", h);
    return () => window.removeEventListener("kairos:prefs-updated", h);
  }, [loadPrefs]);

  // one-time desktop permission prompt (needs a user gesture in Chrome)
  useEffect(() => {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "default") return;
    if (localStorage.getItem("kairos_desktop_prompted")) return;
    const t = setTimeout(() => {
      toast("Enable desktop notifications?", {
        description: "Get MS-Teams-style pop-ups with a soft chime, even when Kairos is in another tab.",
        duration: 15000,
        action: {
          label: "Enable",
          onClick: async () => {
            const p = await Notification.requestPermission();
            if (p === "granted") {
              toast.success("Desktop notifications enabled");
              playChime();
            }
          },
        },
      });
      localStorage.setItem("kairos_desktop_prompted", "1");
    }, 2500);
    return () => clearTimeout(t);
  }, []);

  // SSE connection (EventSource auto-reconnects on drop)
  useEffect(() => {
    const token = localStorage.getItem("kairos_token");
    if (!token) return;
    const es = new EventSource(`${BASE}/notifications/stream?token=${encodeURIComponent(token)}`);

    es.addEventListener("notification", (ev) => {
      let n;
      try { n = JSON.parse(ev.data); } catch { return; }
      const p = prefsRef.current;

      cbRef.current && cbRef.current(n);

      // Live state sync: my_* notifications mean my break state changed —
      // refresh /auth/me instantly instead of waiting for the 15s poll.
      if ((n.category || "").startsWith("my_")) {
        qcRef.current?.invalidateQueries({ queryKey: ["auth-me"] });
      }

      // in-app toast (always — server already filtered muted categories)
      const fn = toast[SEV_TOAST[n.severity] || "info"] || toast;
      fn(n.title || "Notification", { description: n.body || "" });

      // fallback channels: title-bar blink when tab is hidden + persistent
      // in-app banner for important alerts (works even when the OS/corporate
      // policy blocks desktop notifications).
      if (document.hidden) startTitleBlink();
      if (n.severity === "warning" || n.severity === "error") {
        window.dispatchEvent(new CustomEvent("kairos:alert", { detail: n }));
      }

      // OS-level desktop toast
      if (
        p.desktop_enabled !== false &&
        "Notification" in window &&
        Notification.permission === "granted" &&
        (p.desktop_categories || []).includes(n.category)
      ) {
        showDesktopToast(n);
      }

      if (p.sound_enabled !== false) playChime();
    });

    es.addEventListener("group_message", (ev) => {
      try {
        window.dispatchEvent(new CustomEvent("kairos:group-message", { detail: JSON.parse(ev.data) }));
      } catch { /* noop */ }
    });

    es.addEventListener("group_update", (ev) => {
      try {
        window.dispatchEvent(new CustomEvent("kairos:group-update", { detail: JSON.parse(ev.data) }));
      } catch { /* noop */ }
    });

    es.addEventListener("group_typing", (ev) => {
      try {
        window.dispatchEvent(new CustomEvent("kairos:group-typing", { detail: JSON.parse(ev.data) }));
      } catch { /* noop */ }
    });

    es.addEventListener("group_receipt", (ev) => {
      try {
        window.dispatchEvent(new CustomEvent("kairos:group-receipt", { detail: JSON.parse(ev.data) }));
      } catch { /* noop */ }
    });

    es.addEventListener("rta:dirty", () => {
      // Floor state changed somewhere — nudge any open RTMA Console to refresh.
      window.dispatchEvent(new Event("kairos:rta-dirty"));
    });

    es.onerror = () => { /* EventSource retries automatically */ };
    return () => es.close();
  }, []);
}
